import { lazy, startTransition } from 'react';
import { Link, matchPath } from 'react-router';

import { pageurl } from '../constants/pageurl';
import { isChunkLoadError, reloadOnceForStaleBuild } from '../lib/chunk-reload';

export type PreloadableComponent<T extends React.ComponentType<any>> = React.LazyExoticComponent<T> & {
	preload?: () => Promise<void>;
};

export function ReactLazyPreload<T extends React.ComponentType<any>>(
	importStatement: () => Promise<{ default: T }>
): PreloadableComponent<T> {
	// A chunk from a build that has since been replaced 404s; reload once to pick up the new build.
	const load = async () => {
		try {
			return await importStatement();
		} catch (error) {
			if (isChunkLoadError(error)) reloadOnceForStaleBuild();
			throw error;
		}
	};

	const Component = lazy(load) as PreloadableComponent<T>;

	Component.preload = async () =>
		await new Promise<void>((resolve) => {
			startTransition(() => {
				// A failed preload must not hang or surface as an unhandled rejection; the render-time
				// import retries and reaches the ErrorBoundary if it still fails.
				load()
					.catch(() => undefined)
					.then(() => resolve());
			});
		});

	return Component;
}

interface IRouteComponent {
	path: string;
	Component: PreloadableComponent<() => JSX.Element>;
}

export const findComponentForRoute = (
	path: string,
	routes: IRouteComponent[]
): PreloadableComponent<() => JSX.Element> | null => {
	const matchingRoute = routes.find((route) => matchPath(route.path, path));

	return matchingRoute ? matchingRoute.Component : null;
};

const OverviewPage = ReactLazyPreload(async () => await import('../app/overview'));
const SupportDeskPage = ReactLazyPreload(async () => await import('../app/support/pages/SupportDeskPage'));
const HelpHomePage = ReactLazyPreload(async () => await import('../app/help-centre/pages/HelpHomePage'));
const HelpSearchPage = ReactLazyPreload(async () => await import('../app/help-centre/pages/HelpSearchPage'));
const HelpArticlePage = ReactLazyPreload(async () => await import('../app/help-centre/pages/HelpArticlePage'));
const HelpAdminArticlesPage = ReactLazyPreload(async () => await import('../app/help-centre/pages/HelpAdminArticlesPage'));
const HelpArticleEditorPage = ReactLazyPreload(async () => await import('../app/help-centre/pages/HelpArticleEditorPage'));
const AllZonePage = ReactLazyPreload(async () => await import('../app/electoral-divisions/zone/all'));
const AllStatePage = ReactLazyPreload(async () => await import('../app/electoral-divisions/state/all'));
const AllLGAPage = ReactLazyPreload(async () => await import('../app/electoral-divisions/lga/all'));
const AllWardPage = ReactLazyPreload(async () => await import('../app/electoral-divisions/ward/all'));
const AllPollingUnitPage = ReactLazyPreload(async () => await import('../app/electoral-divisions/polling-unit/all'));
const PollingUnitDetailPage = ReactLazyPreload(
	async () => await import('../app/electoral-divisions/polling-unit/detail/PollingUnitDetailPage')
);
const AllTreePage = ReactLazyPreload(async () => await import('../app/electoral-divisions/tree/all'));
const ElectionPage = ReactLazyPreload(async () => await import('../app/election'));
const ElectionCategory = ReactLazyPreload(async () => await import('../app/election-parameters/election-category/all'));
const UsersPage = ReactLazyPreload(async () => await import('../app/system-management/User'));
const RoleActionsPage = ReactLazyPreload(async () => await import('../app/system-management/Role.Action'));
const RolesPage = ReactLazyPreload(async () => await import('../app/system-management/Role'));
const RoleMatrixPage = ReactLazyPreload(async () => await import('../app/system-management/Role.Matrix'));
const ApiKeysPage = ReactLazyPreload(async () => await import('../app/system-management/ApiKey'));
const ApiKeyDocsPage = ReactLazyPreload(async () => await import('../app/system-management/ApiKeyDocs'));
const ServiceRegistryPage = ReactLazyPreload(async () => await import('../app/system-management/ServiceRegistry'));
const TeamAccessPage = ReactLazyPreload(async () => await import('../app/team-access/TeamAccess'));
const MyTeamPage = ReactLazyPreload(async () => await import('../app/my-team/MyTeam'));
const PoliticalPartyPage = ReactLazyPreload(async () => await import('../app/political-party/all'));
const PresidingOfficerPage = ReactLazyPreload(async () => await import('../app/presiding-officer/all'));

const ProfilePage = ReactLazyPreload(async () => await import('../app/profile'));

const ElectionResults = ReactLazyPreload(async () => await import('../app/election-results'));
const AIPage = ReactLazyPreload(async () => await import('../app/ai'));

const ElectionCycle = ReactLazyPreload(async () => await import('../app/election-parameters/election-attribute'));
const Constituency = ReactLazyPreload(async () => await import('../app/electoral-divisions/constituency/all'));
const ConstituencyReportPage = ReactLazyPreload(
	async () => await import('../app/electoral-divisions/constituency/report/ConstituencyReportPage')
);
const ElectoralDivisionsRedirect = ReactLazyPreload(async () => await import('../app/electoral-divisions/redirect'));

const NotificationPage = ReactLazyPreload(async () => await import('../app/system-management/Notification'));
const ReportPage = ReactLazyPreload(async () => await import('../components/report-issues/feedbacks'));
const RequestListPage = ReactLazyPreload(async () => await import('../app/system-management/Request.List'));

// const AgentsPage = ReactLazyPreload(async () => await import('../app/manage-agents'));
const AgentsByElectionIdPage = ReactLazyPreload(async () => await import('../app/manage-agents'));
const AgentsDashboardPage = ReactLazyPreload(async () => await import('../app/manage-agents/dashboard'));
const AgentLiveMapPage = ReactLazyPreload(async () => await import('../app/manage-agents/live-map'));
const AgentCoveragePage = ReactLazyPreload(async () => await import('../app/agent-coverage'));
const AgentsByElectoralDivisionPage = ReactLazyPreload(
	async () => await import('../app/manage-agents/by-electoral-division/by-electoral-division')
);

const AuditTrailPage = ReactLazyPreload(async () => await import('../app/system-management/Audit.Trail'));
const SubscriptionPage = ReactLazyPreload(async () => await import('../app/system-management/Subscription'));
const ElectionType = ReactLazyPreload(async () => await import('../app/election-parameters/election-types'));
const PartyParticipationPage = ReactLazyPreload(async () => await import('../app/election-parameters/party-participation'));

const SettingsPage = ReactLazyPreload(async () => await import('../app/settings'));

const ElectionOfficialsPage = ReactLazyPreload(async () => await import('../app/system-management/Result.Assignment'));

const ResultAnalyticsPage = ReactLazyPreload(async () => await import('../app/result-analytics'));
const ResultAnalyticsAllLevelsPage = ReactLazyPreload(async () => await import('../app/result-analytics/pages/AllLevels'));
const ResultAnalyticsByLevelPage = ReactLazyPreload(async () => await import('../app/result-analytics/pages/ByLevel'));
const ResultAnalyticsByGeographyPage = ReactLazyPreload(async () => await import('../app/result-analytics/pages/ByGeography'));
const ResultAnalyticsLegacyRedirect = ReactLazyPreload(async () => await import('../app/result-analytics/LegacyRedirect'));

const ResultVerificationQueuePage = ReactLazyPreload(async () => await import('../app/result-verification'));
const ResultVerificationDetailPage = ReactLazyPreload(async () => await import('../app/result-verification/pages/Detail'));
const ResultVerificationDisputesPage = ReactLazyPreload(async () => await import('../app/result-verification/pages/Disputes'));

const ResultAuditQueuePage = ReactLazyPreload(async () => await import('../app/result-audit'));
const ResultAuditEntityPage = ReactLazyPreload(async () => await import('../app/result-audit/pages/Entity'));

const IRevExtractionPage = ReactLazyPreload(async () => await import('../app/irev-extraction'));
const ResultPipelinePage = ReactLazyPreload(async () => await import('../app/result-pipeline'));
const AnalystPerformancePage = ReactLazyPreload(async () => await import('../app/analyst-performance'));

const ResultTimeMachinePage = ReactLazyPreload(async () => await import('../app/result-time-machine'));

const VesrLayout = ReactLazyPreload(async () => await import('../app/vesr/layout/VesrLayout'));

const VotersLayout = ReactLazyPreload(async () => await import('../app/voters'));
const LegacyImportVotersRedirect = ReactLazyPreload(async () => await import('../app/voters/LegacyImportRedirect'));
const CandidateRecordsLayout = ReactLazyPreload(async () => await import('../app/candidate-records'));
const MediaGalleryPage = ReactLazyPreload(async () => await import('../app/media-gallery'));
const MessageTemplatesPage = ReactLazyPreload(async () => await import('../app/message-templates'));
const ContactsLayout = ReactLazyPreload(async () => await import('../app/contacts'));

const Organization = ReactLazyPreload(async () => await import('../app/organizations'));

const MyElection = ReactLazyPreload(async () => await import('../app/my-election'));
const LiveElection = ReactLazyPreload(async () => await import('../app/live-election'));
const CancelledUnits = ReactLazyPreload(async () => await import('../app/cancellations'));
const StrongHold = ReactLazyPreload(async () => await import('../app/strong-hold'));

const MyParty = ReactLazyPreload(async () => await import('../app/my-party'));

const MyWallet = ReactLazyPreload(async () => await import('../app/my-wallet'));

const ServiceRates = ReactLazyPreload(async () => await import('../app/system-management/ServiceRates'));

const Coupon = ReactLazyPreload(async () => await import('../app/system-management/Coupon'));

const AdminWalletList = ReactLazyPreload(async () => await import('../app/system-management/wallets/WalletList'));

const VoterInteractionMessaging = ReactLazyPreload(async () => await import('../app/voter-interaction/messaging'));

const ElectionDashboardPage = ReactLazyPreload(async () => await import('../app/dashboard/elections/ElectionDashboardPage'));
const SystemDashboardPage = ReactLazyPreload(async () => await import('../app/dashboard/system-management/SystemDashboardPage'));

const FeedbackPage = ReactLazyPreload(async () => await import('../app/feedback/index'));

const DocumentTypesPage = ReactLazyPreload(async () => await import('../app/system-management/DocumentTypes'));
const LegalDocumentsPage = ReactLazyPreload(async () => await import('../app/system-management/LegalDocuments'));
const LegalConsentsPage = ReactLazyPreload(async () => await import('../app/system-management/LegalConsents'));

const ChannelGovernancePage = ReactLazyPreload(async () => await import('../app/channel-governance'));

const LmsCoursesPage = ReactLazyPreload(async () => await import('../app/system-management/LmsCourses'));

const ElectionObserver = ReactLazyPreload(async () => await import('../app/election-observer/index'));
const ManageObserversPage = ReactLazyPreload(async () => await import('../app/election-observer/manage-observers'));
const ObserverLiveMapPage = ReactLazyPreload(async () => await import('../app/election-observer/live-map'));
const ObserverDashboardPage = ReactLazyPreload(async () => await import('../app/election-observer/dashboard'));
const ObserverDeskPage = ReactLazyPreload(async () => await import('../app/election-observer/desk'));
const ObserversByElectoralDivisionPage = ReactLazyPreload(
	async () => await import('../app/election-observer/by-electoral-division')
);

export const routeComponent: IRouteComponent[] = [
	{ path: pageurl.OVERVIEW, Component: OverviewPage },
	{ path: pageurl.SUPPORT, Component: SupportDeskPage },
	// Help Centre. The literal `search` / `manage` segments outrank `:slug` in react-router's ranking.
	{ path: pageurl.HELP, Component: HelpHomePage },
	{ path: pageurl.HELP_SEARCH, Component: HelpSearchPage },
	{ path: pageurl.HELP_MANAGE, Component: HelpAdminArticlesPage },
	{ path: `${pageurl.HELP_MANAGE}/:id`, Component: HelpArticleEditorPage },
	{ path: `${pageurl.HELP}/:slug`, Component: HelpArticlePage },
	{ path: pageurl.ELECTIONOFFICIALS, Component: ElectionOfficialsPage },
	{ path: pageurl.SETTINGS, Component: SettingsPage },
	{ path: pageurl.NOTIFICATIONS, Component: NotificationPage },
	{ path: pageurl.ZONES, Component: AllZonePage },
	{ path: pageurl.STATES, Component: AllStatePage },
	{ path: pageurl.LGAS, Component: AllLGAPage },
	{ path: pageurl.WARDS, Component: AllWardPage },
	{ path: pageurl.POLLINGUNITS, Component: AllPollingUnitPage },
	{ path: pageurl.POLLINGUNIT_DETAIL, Component: PollingUnitDetailPage },
	{ path: pageurl.PROFILE, Component: ProfilePage },
	{ path: pageurl.ELECTIONS, Component: ElectionPage },
	{ path: pageurl.ELECTIVECATEGORY, Component: ElectionCategory },
	{ path: pageurl.ELECTIONTYPE, Component: ElectionType },
	{ path: pageurl.CONSTITUENCY, Component: Constituency },
	{ path: `${pageurl.CONSTITUENCY_REPORT}/:constituencyId`, Component: ConstituencyReportPage },
	{ path: pageurl.TREE, Component: AllTreePage },
	{ path: pageurl.ELECTORALDIVISIONS, Component: ElectoralDivisionsRedirect },
	{ path: pageurl.USERS, Component: UsersPage },
	{ path: pageurl.REPORT, Component: ReportPage },
	{ path: pageurl.ROLES, Component: RolesPage },
	{ path: pageurl.ROLES_MATRIX, Component: RoleMatrixPage },
	{ path: pageurl.API_KEYS, Component: ApiKeysPage },
	{ path: pageurl.API_KEYS_DOCS, Component: ApiKeyDocsPage },
	{ path: pageurl.SERVICE_REGISTRY, Component: ServiceRegistryPage },
	{ path: pageurl.TEAM_ACCESS, Component: TeamAccessPage },
	{ path: pageurl.ROLEACTIONS, Component: RoleActionsPage },
	{ path: pageurl.POLITICALPARTY, Component: PoliticalPartyPage },
	{ path: pageurl.PRESIDINGOFFICER, Component: PresidingOfficerPage },
	{ path: pageurl.RESULTS, Component: ElectionResults },
	{ path: pageurl.AI, Component: AIPage },
	{ path: pageurl.ELECTIONCYCLE, Component: ElectionCycle },
	{ path: pageurl.PARTY_PARTICIPATION, Component: PartyParticipationPage },
	{ path: pageurl.REQUESTLIST, Component: RequestListPage },
	{ path: pageurl.MANAGEAGENTS, Component: AgentsByElectionIdPage },
	{ path: pageurl.MANAGEAGENTSDASHBOARD, Component: AgentsDashboardPage },
	{ path: pageurl.MANAGEAGENTS_LIVEMAP, Component: AgentLiveMapPage },
	{ path: pageurl.MANAGEAGENTS_COVERAGE, Component: AgentCoveragePage },
	{ path: `${pageurl.MANAGEAGENTS}/:electoralDivision`, Component: AgentsByElectoralDivisionPage },
	{ path: pageurl.AUDITTRAIL, Component: AuditTrailPage },
	{ path: pageurl.RESULTNOTIFICATIONS, Component: SubscriptionPage },
	{ path: pageurl.RESULTNOTIFICATIONS, Component: SubscriptionPage },
	{ path: pageurl.RESULTANALYTICS, Component: ResultAnalyticsPage },
	{ path: pageurl.RESULTANALYTICS_ALL_LEVELS, Component: ResultAnalyticsAllLevelsPage },
	{ path: pageurl.RESULTANALYTICS_BY_LEVEL, Component: ResultAnalyticsByLevelPage },
	{ path: pageurl.RESULTANALYTICS_BY_GEOGRAPHY, Component: ResultAnalyticsByGeographyPage },
	{ path: pageurl.RESULTANALYTICS_LEGACY, Component: ResultAnalyticsLegacyRedirect },
	{ path: `${pageurl.RESULTANALYTICS_LEGACY}/*`, Component: ResultAnalyticsLegacyRedirect },
	{ path: pageurl.RESULTVERIFICATION, Component: ResultVerificationQueuePage },
	{ path: `${pageurl.RESULTVERIFICATION_DETAIL}/:level/:code`, Component: ResultVerificationDetailPage },
	{ path: pageurl.RESULTVERIFICATION_DISPUTES, Component: ResultVerificationDisputesPage },
	{ path: pageurl.RESULTAUDIT, Component: ResultAuditQueuePage },
	{ path: `${pageurl.RESULTAUDIT_ENTITY}/:level/:code`, Component: ResultAuditEntityPage },
	{ path: pageurl.IREVEXTRACTION, Component: IRevExtractionPage },
	{ path: pageurl.RESULTPIPELINE, Component: ResultPipelinePage },
	{ path: pageurl.ANALYSTPERFORMANCE, Component: AnalystPerformancePage },
	{ path: pageurl.RESULTTIMEMACHINE, Component: ResultTimeMachinePage },
	{ path: pageurl.ORGANIZATIONS, Component: Organization },
	{ path: pageurl.MYELECTION, Component: MyElection },
	{ path: pageurl.LIVEELECTION, Component: LiveElection },
	{ path: pageurl.STRONGHOLD, Component: StrongHold },
	{ path: pageurl.CANCELLED_UNITS, Component: CancelledUnits },
	{ path: pageurl.MYPARTY, Component: MyParty },
	// Voters workspace — the layout owns Register (index) / import / history.
	{ path: `${pageurl.VOTERS}/*`, Component: VotersLayout },
	{ path: pageurl.IMPORTVOTERS, Component: LegacyImportVotersRedirect },
	// Candidate Records — the layout owns registry (index) / import / seat / trail.
	{ path: `${pageurl.CANDIDATE_RECORDS}/*`, Component: CandidateRecordsLayout },
	// One row; the layout owns `index` / `:listId` / `import` internally, so `/import` can never be
	// swallowed by the `:listId` segment through route ordering.
	{ path: `${pageurl.CONTACTS}/*`, Component: ContactsLayout },
	{ path: pageurl.MEDIA_GALLERY, Component: MediaGalleryPage },
	{ path: pageurl.MESSAGE_TEMPLATES, Component: MessageTemplatesPage },
	{ path: pageurl.MYWALLET, Component: MyWallet },
	{ path: pageurl.MY_TEAM, Component: MyTeamPage },
	{ path: '/admin/vesr/*', Component: VesrLayout },
	{ path: pageurl.SERVICE_RATE, Component: ServiceRates },
	{ path: pageurl.COUPON, Component: Coupon },
	{ path: pageurl.ADMIN_WALLETS, Component: AdminWalletList },
	{ path: `${pageurl.VOTER_INTERACTIONS}/messaging`, Component: VoterInteractionMessaging },
	{ path: pageurl.ELECTIONS_DASHBOARD, Component: ElectionDashboardPage },
	{ path: pageurl.MANAGEMENT_DASHBOARD, Component: SystemDashboardPage },
	{ path: pageurl.FEEDBACK, Component: FeedbackPage },
	{ path: pageurl.LEGAL_DOCUMENT_TYPES, Component: DocumentTypesPage },
	{ path: pageurl.LEGAL_DOCUMENTS, Component: LegalDocumentsPage },
	{ path: pageurl.LEGAL_CONSENTS, Component: LegalConsentsPage },
	{ path: pageurl.CHANNEL_GOVERNANCE, Component: ChannelGovernancePage },
	{ path: pageurl.LMS_COURSES, Component: LmsCoursesPage },
	{
		path: pageurl.ELECTIONOBSERVER,
		Component: ElectionObserver,
	},
	{ path: pageurl.OBSERVER_DASHBOARD, Component: ObserverDashboardPage },
	{ path: pageurl.OBSERVER_DESK, Component: ObserverDeskPage },
	{ path: pageurl.MANAGE_OBSERVERS, Component: ManageObserversPage },
	// Before the `:electoralDivision` route, so `live-map` is never read as a division name.
	{ path: pageurl.OBSERVER_LIVEMAP, Component: ObserverLiveMapPage },
	{ path: `${pageurl.MANAGE_OBSERVERS}/:electoralDivision`, Component: ObserversByElectoralDivisionPage },
];

export const preloadRouteComponent = (path: string) => {
	const component = findComponentForRoute(path, routeComponent);

	if (component?.preload) {
		component.preload();
	}
};

export const PreloadLink = ({ to, ...rest }: { to: any }) => (
	<Link
		to={to}
		onMouseEnter={() => preloadRouteComponent(to as string)}
		{...rest}
	/>
);
